<script setup lang="ts">
import { computed, reactive, watch } from 'vue'
import AppCard from '@/components/ui/AppCard.vue'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import type { CatalogFormPayload, CatalogKind, SetType } from '@/services/catalog'

/**
 * Formulario de alta y edición de un elemento del catálogo.
 *
 * El tipo **no se elige aquí**: lo dice la ruta de la que se viene.
 * `/catalog/sets/create` es un alta de set y `/catalog/products/create` una de
 * producto. Ofrecer un selector en un sitio donde el tipo ya está decidido
 * Plantaba dos cosas: que se pudiera cambiar de tipo sin querer, y que el botón
 * guardara contra un endpoint distinto del que decía el título.
 *
 * Create y Edit comparten este componente porque el formulario y las reglas son
 * los mismos; lo único que cambia es el texto del botón y si se rellenan los
 * valores. Duplicar el formulario en dos archivos haría que una corrección en la
 * validación se aplicara en uno y se olvidara en el otro.
 *
 * La validación del cliente espeja la del backend solo en lo que el usuario
 * puede corregir sin viaje de ida y vuelta: obligatoriedad, formato de precio y
 * longitud. Las reglas que dependen del estado del servidor (unicidad de
 * `reference`, rango de precio frente a otros registros) no se replican; las
 * devuelve el 422 y se pintan desde `serverErrors`.
 */

interface CatalogFormValues {
  reference: string
  name: string
  retail_price: string
  wholesale_price: string
  type: SetType
}

const props = withDefaults(
  defineProps<{
    /** Lo dice la ruta. El formulario no lo cambia ni deja cambiarlo. */
    kind: CatalogKind
    values?: CatalogFormValues
    submitLabel: string
    loading?: boolean
    serverErrors?: Record<string, string[]>
  }>(),
  { loading: false, serverErrors: () => ({}) },
)

const emit = defineEmits<{
  submit: [payload: CatalogFormPayload]
  cancel: []
}>()

const typeOptions: Array<{ label: string; value: SetType }> = [
  { label: 'Nacional', value: 'nacional' },
  { label: 'Premium', value: 'premium' },
]

function emptyForm(): CatalogFormValues {
  return { reference: '', name: '', retail_price: '', wholesale_price: '', type: 'nacional' }
}

const form = reactive<CatalogFormValues>({ ...(props.values ?? emptyForm()) })

// Al editar, el registro llega del backend después de montar la vista. Copiarlo
// una sola vez evita que un `reset` posterior vacíe lo que el usuario ya ve.
watch(
  () => props.values,
  (next) => {
    if (!next) return
    Object.assign(form, next)
  },
)

/**
 * Un precio es un entero, sin centavos.
 *
 * El patrón va en la misma línea que el backend, que lo rechaza con `integer`: si
 * aquí se aceptara `100.50` se llegaría al final del formulario para recibir un 422
 * que el comercial no entiende, cuando el error se podía ver mientras escribía.
 */
const PRICE_PATTERN = /^\d+$/
const MAX_PRICE = 999999999

/** `0` a `999999999`, el rango que acepta el backend. */
function validatePrice(value: string): string | null {
  const trimmed = value.trim()
  if (trimmed === '') return 'El precio es obligatorio'
  if (!PRICE_PATTERN.test(trimmed)) return 'El precio tiene que ser un número entero, sin decimales'
  if (Number.parseInt(trimmed, 10) > MAX_PRICE) return 'El precio no puede superar 999999999'
  return null
}

const fields = ['type', 'reference', 'name', 'retail_price', 'wholesale_price'] as const

/**
 * El mensaje pintado combina error de cliente y de servidor: si el backend
 * rechaza por referencia duplicada, ese mensaje gana porque explica la causa
 * real, que el cliente no puede conocer de antemano.
 */
const messages = computed<Record<(typeof fields)[number], string | null>>(() => {
  const reference = form.reference.trim()
  const name = form.name.trim()

  const client: Record<(typeof fields)[number], string | null> = {
    type: props.kind === 'set' && !form.type ? 'El tipo es obligatorio' : null,
    reference: reference === '' ? 'La referencia es obligatoria' : reference.length > 255 ? 'Máximo 255 caracteres' : null,
    name: name === '' ? 'El nombre es obligatorio' : name.length > 255 ? 'Máximo 255 caracteres' : null,
    retail_price: validatePrice(form.retail_price),
    wholesale_price: validatePrice(form.wholesale_price),
  }

  const result = {} as Record<(typeof fields)[number], string | null>
  for (const field of fields) {
    const fromServer = props.serverErrors[field]
    result[field] = fromServer && fromServer.length > 0 ? fromServer[0] : client[field]
  }
  return result
})

const hasErrors = computed(() => fields.some((field) => messages.value[field] !== null))

function handleSubmit(): void {
  if (hasErrors.value) return

  const payload: CatalogFormPayload = {
    kind: props.kind,
    reference: form.reference.trim(),
    name: form.name.trim(),
    retail_price: Number.parseFloat(form.retail_price),
    wholesale_price: Number.parseFloat(form.wholesale_price),
  }

  // `type` solo viaja en sets. Mandarlo en un producto haría que el cliente
  // inventara un campo que el backend descarta en silencio.
  if (props.kind === 'set') payload.type = form.type

  emit('submit', payload)
}
</script>

<template>
  <AppCard>
    <template #header>
      <h2 class="text-lg font-semibold text-surface-900 dark:text-surface-100">
        {{ kind === 'set' ? 'Datos del set' : 'Datos del producto' }}
      </h2>
    </template>

    <form
      class="space-y-5"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <div
        v-if="kind === 'set'"
        class="space-y-2"
      >
        <label
          for="catalog-type"
          class="block text-sm font-medium text-surface-700 dark:text-surface-300"
        >
          Tipo <span class="text-red-500">*</span>
        </label>
        <Select
          id="catalog-type"
          v-model="form.type"
          :options="typeOptions"
          option-label="label"
          option-value="value"
          placeholder="Selecciona un tipo"
          class="w-full"
          :invalid="!!messages.type"
        />
        <p
          v-if="messages.type"
          class="text-sm text-red-600 dark:text-red-400"
        >
          {{ messages.type }}
        </p>
      </div>

      <div class="space-y-2">
        <label
          for="catalog-reference"
          class="block text-sm font-medium text-surface-700 dark:text-surface-300"
        >
          Referencia <span class="text-red-500">*</span>
        </label>
        <InputText
          id="catalog-reference"
          v-model="form.reference"
          placeholder="PROD-001"
          class="w-full"
          :invalid="!!messages.reference"
          maxlength="255"
        />
        <p
          v-if="messages.reference"
          class="text-sm text-red-600 dark:text-red-400"
        >
          {{ messages.reference }}
        </p>
      </div>

      <div class="space-y-2">
        <label
          for="catalog-name"
          class="block text-sm font-medium text-surface-700 dark:text-surface-300"
        >
          Nombre <span class="text-red-500">*</span>
        </label>
        <InputText
          id="catalog-name"
          v-model="form.name"
          placeholder="Nombre comercial"
          class="w-full"
          :invalid="!!messages.name"
          maxlength="255"
        />
        <p
          v-if="messages.name"
          class="text-sm text-red-600 dark:text-red-400"
        >
          {{ messages.name }}
        </p>
      </div>

      <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div class="space-y-2">
          <label
            for="catalog-retail"
            class="block text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            Precio minorista <span class="text-red-500">*</span>
          </label>
          <InputText
            id="catalog-retail"
            v-model="form.retail_price"
            placeholder="0.00"
            input-mode="decimal"
            class="w-full"
            :invalid="!!messages.retail_price"
          />
          <p
            v-if="messages.retail_price"
            class="text-sm text-red-600 dark:text-red-400"
          >
            {{ messages.retail_price }}
          </p>
        </div>

        <div class="space-y-2">
          <label
            for="catalog-wholesale"
            class="block text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            Precio mayorista <span class="text-red-500">*</span>
          </label>
          <InputText
            id="catalog-wholesale"
            v-model="form.wholesale_price"
            placeholder="0.00"
            input-mode="decimal"
            class="w-full"
            :invalid="!!messages.wholesale_price"
          />
          <p
            v-if="messages.wholesale_price"
            class="text-sm text-red-600 dark:text-red-400"
          >
            {{ messages.wholesale_price }}
          </p>
        </div>
      </div>

      <div class="flex flex-col-reverse gap-3 border-t border-surface-200 pt-5 sm:flex-row sm:justify-end dark:border-surface-700">
        <Button
          type="button"
          label="Cancelar"
          severity="secondary"
          :disabled="loading"
          @click="emit('cancel')"
        />
        <Button
          type="submit"
          :label="submitLabel"
          icon="pi pi-check"
          :loading="loading"
        />
      </div>
    </form>
  </AppCard>
</template>
